import { useCallback, useEffect, useRef, useState } from 'react'
import type * as Y from 'yjs'
import { api } from './api'
import { parseSections } from './sections'
import { proseAnchors, type Anchor } from './anchors'
import { withBackgroundRoom } from './wide-replace'
import type { PartKind } from './types'
// Types only: the deck's code is the lazily loaded editor's chunk.
import type { DeckAnchor } from '../slides/anchors'

/**
 * The anchors of an artefact (AB#350): every named anchor of its prose and slides parts (a grid has
 * none), for the link windows' anchor picker (components/AnchorPicker.tsx), the uniqueness of a new
 * name, and a followed anchor link (lib/anchor-nav.ts).
 *
 * The part open in this tab is read off its live Y.Doc; every OTHER part's room is opened in the
 * background just long enough to read it (lib/wide-replace.ts#withBackgroundRoom, the AB#326
 * connection) — read-only: nothing is claimed, locked or written.
 */

/** One anchor of the artefact, with where it sits. */
export interface ArtefactAnchor extends Anchor {
  /** The part's room key ("bid/artefact/part"). */
  room: string
  partTitle: string
  partKind: 'prose' | 'slides'
  /** 'text': letters; 'element': a whole slide element. */
  on: 'text' | 'element'
  /** prose: the § number of the section it sits in ('' before the first heading). */
  section?: string
  /** slides: the slide holding it, and its 0-based position in the deck. */
  slideId?: string
  slideIndex?: number
  /** slides: the element holding it (the anchored one, or the one whose letters are anchored). */
  elementId?: string
  /** slides, `on: 'element'`: the element's kind. */
  elementKind?: string
  /** A short excerpt of the anchored text (plain), '' for a whole element. */
  excerpt: string
}

/** The part open in this tab: read live, never reopened. */
export interface OpenPart {
  room: string
  ydoc: Y.Doc
}

/** An excerpt is cut to about this many characters. */
const EXCERPT_LEN = 60

/** Markdown inline text as plain words: the inline syntaxes (colour, anchor, link, emphasis) removed. */
export function plainInline(md: string): string {
  return md
    .replace(/\]\{(?:color|anchor)=(?:[^"}]|"(?:[^"\\]|\\.)*")*\}/g, ']')
    .replace(/==\{[^}]*\}/g, '')
    .replace(/\]\([^)]*\)/g, ']')
    // The syntax goes; an escaped character is a literal, kept (then unescaped).
    .replace(/(?<!\\)(?:[[\]*_`~]|==)/g, '')
    .replace(/\\([\\`*_[\]{}()#+\-.!=~|])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

/** `s` cut to about EXCERPT_LEN characters. */
export const excerptOf = (s: string): string => {
  const t = s.replace(/\s+/g, ' ').trim()
  return t.length > EXCERPT_LEN ? `${t.slice(0, EXCERPT_LEN).trimEnd()}…` : t
}

/** The anchors of one prose part's markdown, each with the § it sits in. */
export function proseArtefactAnchors(md: string, offset: number, part: { room: string; title: string }): ArtefactAnchor[] {
  const sections = parseSections(md, offset)
  return proseAnchors(md).map((a) => {
    // The innermost § holding the anchor: the last one starting at or before it that has not ended.
    let section = ''
    for (const s of sections) if (s.start <= a.offset && a.offset < s.end) section = s.number
    return { id: a.id, name: a.name, room: part.room, partTitle: part.title, partKind: 'prose', on: 'text', section, excerpt: excerptOf(plainInline(a.text)) }
  })
}

/** The anchors of one deck, as the artefact lists them. */
export function deckArtefactAnchors(list: DeckAnchor[], part: { room: string; title: string }): ArtefactAnchor[] {
  return list.map((a) => ({
    id: a.id,
    name: a.name,
    room: part.room,
    partTitle: part.title,
    partKind: 'slides',
    on: a.on,
    slideId: a.slideId,
    slideIndex: a.slideIndex,
    elementId: a.elementId,
    ...(a.elementKind ? { elementKind: a.elementKind } : {}),
    excerpt: a.on === 'text' ? excerptOf(a.text ?? '') : '',
  }))
}

/** Read the anchors of a synced room. */
async function readRoomAnchors(ydoc: Y.Doc, part: { room: string; title: string; kind?: PartKind }): Promise<ArtefactAnchor[]> {
  if (part.kind === 'slides' || ydoc.getMap('meta').get('kind') === 'slides') {
    const { deckAnchors } = await import('../slides/anchors')
    return deckArtefactAnchors(deckAnchors(ydoc), part)
  }
  const { offset } = await api.sections(part.room).catch(() => ({ offset: 0 }))
  return proseArtefactAnchors(ydoc.getText('content').toString(), offset, part)
}

export interface ArtefactAnchors {
  anchors: ArtefactAnchor[]
  /** Titles of the parts that could not be read (a timeout, a refusal): their anchors are missing. */
  failed: string[]
  /** The parts read, in the artefact's order. */
  parts: Array<{ room: string; title: string; kind?: PartKind }>
}

/**
 * Every anchor of the artefact holding `open.room`, part by part in the artefact's order. The parts
 * are read in parallel (an artefact holds a handful of parts).
 */
export async function collectArtefactAnchors(open: OpenPart, wsUrl: string): Promise<ArtefactAnchors> {
  const [bidId, artefactId] = open.room.split('/')
  if (!bidId) return { anchors: [], failed: [], parts: [] }
  const { artefacts } = await api.artefacts(bidId)
  const artefact = artefacts.find((a) => a.artefactId === artefactId)
  const parts = (artefact?.parts ?? []).filter((p) => p.kind !== 'grid').map((p) => ({ room: p.name, title: p.title, ...(p.kind ? { kind: p.kind } : {}) }))
  if (!parts.some((p) => p.room === open.room)) parts.push({ room: open.room, title: open.room.split('/')[2] ?? open.room })
  const failed: string[] = []
  const lists = await Promise.all(
    parts.map(async (part) => {
      if (part.room === open.room) return readRoomAnchors(open.ydoc, part)
      const r = await withBackgroundRoom(part.room, wsUrl, (ydoc) => readRoomAnchors(ydoc, part))
      if (r.ok) return r.value
      failed.push(part.title)
      return []
    }),
  )
  return { anchors: lists.flat(), failed, parts }
}

/**
 * The artefact's anchors, for a window that lists them: read once when `open` is set, the open part
 * re-read live as it changes (a member adds an anchor while the window is up). `null` open: nothing.
 */
export function useArtefactAnchors(open: OpenPart | null, wsUrl: string): {
  anchors: ArtefactAnchor[] | null
  failed: string[]
  reload: () => void
} {
  const [state, setState] = useState<ArtefactAnchors | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state
  const [n, setN] = useState(0)
  const reload = useCallback(() => setN((v) => v + 1), [])
  const room = open?.room ?? null
  const openRef = useRef(open)
  openRef.current = open
  useEffect(() => {
    const o = openRef.current
    if (!o) return
    let alive = true
    collectArtefactAnchors(o, wsUrl)
      .then((r) => alive && setState(r))
      .catch(() => alive && setState({ anchors: [], failed: [], parts: [] }))
    return () => {
      alive = false
    }
  }, [room, wsUrl, n])
  // The open part, live: its anchors are swapped in place when its document changes.
  useEffect(() => {
    const o = openRef.current
    if (!o) return
    let timer: ReturnType<typeof setTimeout> | undefined
    let alive = true
    const onUpdate = (): void => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const part = stateRef.current?.parts.find((p) => p.room === o.room)
        if (!part) return
        void readRoomAnchors(o.ydoc, part).then((fresh) => {
          if (!alive) return
          setState((cur) => {
            if (!cur) return cur
            // In the part's place: after the anchors of the parts before it.
            const order = cur.parts.map((p) => p.room)
            const mine = order.indexOf(o.room)
            const before = cur.anchors.filter((a) => order.indexOf(a.room) < mine)
            const after = cur.anchors.filter((a) => order.indexOf(a.room) > mine)
            return { ...cur, anchors: [...before, ...fresh, ...after] }
          })
        })
      }, 200)
    }
    o.ydoc.on('update', onUpdate)
    return () => {
      alive = false
      clearTimeout(timer)
      o.ydoc.off('update', onUpdate)
    }
  }, [room])
  return { anchors: state?.anchors ?? null, failed: state?.failed ?? [], reload }
}
